import { MainScope, Scope } from '@oneview/constants/lookups';
import { createQueryFn } from '@oneview/redux/utils';
import {
    formatAccountSizesBreakdownChart,
    formatAccountTypesBreakdownChart,
    formatMarketCapBreakdown,
    formatSectorsBreakdownDoughnutChartWithEtf,
    formatTopPositionsChartData,
} from '@oneview/utils';
import {
    AccountSizesBreakdownStat,
    AccountTypesBreakdownStat,
    MarketCapBreakdownStat,
    SectorsBreakdownStat,
    TopPositionsStat,
    TopPositionsType,
} from '@orbis-systems/orbis-api-middleware';
import { accountApi } from './accountApi';
import { api } from './baseApi';
import { branchApi } from './branchApi';
import { groupApi } from './groupApi';
import { modelApi } from './modelApi';
import { quoteWatchmanApi } from './quoteWatchmanApi';
import { userApi } from './userApi';

export const chartApi = api.injectEndpoints({
    endpoints: (build) => ({
        getSectorsBreakdownChart: build.query({
            queryFn: createQueryFn(
                async (
                    { scope, id, formatted = true }: { scope: MainScope; id: number; formatted?: boolean },
                    { dispatch },
                ) => {
                    let data: SectorsBreakdownStat;
                    switch (scope) {
                        case Scope.Model:
                            data = await dispatch(modelApi.endpoints.getModelSectorsBreakdown.initiate(id)).unwrap();
                            break;
                        case Scope.Account:
                            data = await dispatch(
                                accountApi.endpoints.getAccountSectorsBreakdown.initiate(id),
                            ).unwrap();
                            break;
                        case Scope.Group:
                            data = await dispatch(groupApi.endpoints.getGroupSectorsBreakdown.initiate(id)).unwrap();
                            break;
                        default:
                            data = await dispatch(branchApi.endpoints.getBranchSectorsBreakdown.initiate()).unwrap();
                    }

                    return formatted ? formatSectorsBreakdownDoughnutChartWithEtf(data) : data;
                },
            ),
        }),
        getAccountSizesDistributionChart: build.query({
            queryFn: createQueryFn(async ({ scope, id }: { scope: MainScope; id: number | string }, { dispatch }) => {
                let data: AccountSizesBreakdownStat;
                switch (scope) {
                    case Scope.Model:
                        data = await dispatch(
                            modelApi.endpoints.getModelAccountSizesBreakdown.initiate(Number(id)),
                        ).unwrap();
                        break;
                    case Scope.Group:
                        data = await dispatch(
                            groupApi.endpoints.getGroupAccountSizesBreakdown.initiate(Number(id)),
                        ).unwrap();
                        break;
                    case Scope.Quote:
                        data = await dispatch(
                            quoteWatchmanApi.endpoints.getQuoteAccountSizesBreakdown.initiate(String(id)),
                        ).unwrap();
                        break;
                    default:
                        data = await dispatch(branchApi.endpoints.getBranchAccountSizesBreakdown.initiate()).unwrap();
                }

                return formatAccountSizesBreakdownChart(data);
            }),
        }),
        getAccountTypesBreakdownChart: build.query({
            queryFn: createQueryFn(async ({ scope, id }: { scope: MainScope; id: number | string }, { dispatch }) => {
                let data: AccountTypesBreakdownStat;
                switch (scope) {
                    case Scope.Model:
                        data = await dispatch(
                            modelApi.endpoints.getModelAccountTypesBreakdown.initiate(Number(id)),
                        ).unwrap();
                        break;
                    case Scope.Group:
                        data = await dispatch(
                            groupApi.endpoints.getGroupAccountTypesBreakdown.initiate(Number(id)),
                        ).unwrap();
                        break;
                    case Scope.User:
                        data = await dispatch(userApi.endpoints.getUserAccountTypesBreakdown.initiate()).unwrap();
                        break;
                    case Scope.Quote:
                        data = await dispatch(
                            quoteWatchmanApi.endpoints.getQuoteAccountTypesBreakdown.initiate(String(id)),
                        ).unwrap();
                        break;
                    default:
                        data = await dispatch(branchApi.endpoints.getBranchAccountTypesBreakdown.initiate()).unwrap();
                }

                return formatAccountTypesBreakdownChart(data);
            }),
        }),
        getMarketCapBreakdownChart: build.query({
            queryFn: createQueryFn(async ({ scope, id }: { scope: MainScope; id: number }, { dispatch }) => {
                let data: MarketCapBreakdownStat;
                switch (scope) {
                    case Scope.Model:
                        data = await dispatch(modelApi.endpoints.getModelMarketCapBreakdown.initiate(id)).unwrap();
                        break;
                    case Scope.Group:
                        data = await dispatch(groupApi.endpoints.getGroupMarketCapBreakdown.initiate(id)).unwrap();
                        break;
                    case Scope.Account:
                        data = await dispatch(accountApi.endpoints.getAccountMarketCapBreakdown.initiate(id)).unwrap();
                        break;
                    default:
                        data = await dispatch(branchApi.endpoints.getBranchMarketCapBreakdown.initiate()).unwrap();
                }

                return formatMarketCapBreakdown(data);
            }),
        }),
        getTopPositionsChart: build.query({
            queryFn: createQueryFn(
                async ({ scope, id, by }: { scope: MainScope; id: number; by?: TopPositionsType }, { dispatch }) => {
                    let data: TopPositionsStat;
                    switch (scope) {
                        case Scope.Model:
                            data = await dispatch(
                                modelApi.endpoints.getModelTopPositionsBreakdown.initiate({ modelId: id, by }),
                            ).unwrap();
                            break;
                        case Scope.Group:
                            data = await dispatch(
                                groupApi.endpoints.getGroupTopPositionsBreakdown.initiate({ groupId: id, by }),
                            ).unwrap();
                            break;
                        case Scope.Account:
                            data = await dispatch(
                                accountApi.endpoints.getAccountTopPositionsBreakdown.initiate({ accountId: id, by }),
                            ).unwrap();
                            break;
                        default:
                            data = await dispatch(
                                branchApi.endpoints.getBranchTopPositionsBreakdown.initiate(by),
                            ).unwrap();
                    }

                    return formatTopPositionsChartData(data);
                },
            ),
        }),
    }),
});

export const {
    useGetSectorsBreakdownChartQuery,
    useGetAccountSizesDistributionChartQuery,
    useGetAccountTypesBreakdownChartQuery,
    useGetMarketCapBreakdownChartQuery,
    useGetTopPositionsChartQuery,
} = chartApi;
